import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CONTENT = {
  about: {
    title: 'Hakkımızda',
    body: `<p>Sosyal.Team, yapay zeka destekli içerik üretim ve sosyal medya dağıtım platformudur.</p>
<p>Misyonumuz, markaların ve içerik üreticilerinin sosyal medya varlıklarını tek bir platformdan yönetmelerini sağlamaktır. AI teknolojileri kullanarak metin, görsel, video ve ses içerikleri saniyeler içinde üretiyoruz.</p>
<p>Türkiye merkezli ekibimiz, futbol medyası ve spor içerik üretimi konusunda uzmanlaşmıştır. Platform, çoklu bayi (multi-tenant) mimarisi ile ajansların ve markaların ihtiyaçlarına uygun olarak tasarlanmıştır.</p>`
  },
  privacy: {
    title: 'Gizlilik Politikası',
    body: `<h3>Kişisel Verilerin Korunması</h3>
<p>Sosyal.Team olarak kullanıcılarımızın gizliliğine önem veriyoruz. Bu politika, kişisel verilerinizin nasıl toplandığını, kullanıldığını ve korunduğunu açıklar.</p>
<h3>Toplanan Veriler</h3>
<p>Platformumuza kayıt olduğunuzda e-posta adresiniz, adınız ve şifreniz (şifrelenmiş olarak) saklanır. Sosyal medya hesaplarınızı bağladığınızda, erişim tokenları şifrelenmiş olarak depolanır.</p>
<h3>Verilerin Kullanımı</h3>
<p>Verileriniz yalnızca hizmet sunumu amacıyla kullanılır. Üçüncü taraflarla paylaşılmaz veya satılmaz.</p>
<h3>Veri Güvenliği</h3>
<p>Tüm veriler şifrelenmiş bağlantılar üzerinden iletilir. Sosyal medya tokenları AES şifreleme ile korunur.</p>
<h3>İletişim</h3>
<p>Gizlilik politikamız hakkında sorularınız için info@sosyal.team adresine yazabilirsiniz.</p>`
  },
  terms: {
    title: 'Kullanım Koşulları',
    body: `<h3>Hizmet Koşulları</h3>
<p>Sosyal.Team platformunu kullanarak aşağıdaki koşulları kabul etmiş olursunuz.</p>
<h3>Hesap Sorumluluğu</h3>
<p>Hesabınızın güvenliğinden siz sorumlusunuz. Şifrenizi güvenli tutun ve başkalarıyla paylaşmayın.</p>
<h3>Kabul Edilebilir Kullanım</h3>
<p>Platform yalnızca yasal amaçlar için kullanılabilir. Spam, yanıltıcı içerik veya kötü amaçlı aktiviteler yasaktır.</p>
<h3>İçerik Sorumluluğu</h3>
<p>AI tarafından üretilen içerikler yol gösterici niteliktedir. Yayınlamadan önce içerikleri gözden geçirmeniz önerilir.</p>
<h3>Kredi Sistemi</h3>
<p>Kredi tabanlı sistem kullanılmaktadır. Kullanılmayan krediler bir sonraki aya devredilmez.</p>
<h3>Hizmet Değişiklikleri</h3>
<p>Sosyal.Team, hizmette değişiklik yapma hakkını saklı tutar. Önemli değişikliklerden kullanıcılar bilgilendirilir.</p>`
  },
  cookies: {
    title: 'Çerez Politikası',
    body: `<h3>Çerez Kullanımı</h3>
<p>Sosyal.Team, platformun düzgün çalışması için çerezler kullanır.</p>
<h3>Zorunlu Çerezler</h3>
<p>Oturum yönetimi ve güvenlik amacıyla zorunlu çerezler kullanılır. Bu çerezler olmadan platforma giriş yapamazsınız.</p>
<h3>Analiz Çerezleri</h3>
<p>Platform kullanımını analiz etmek için anonim çerezler kullanılabilir.</p>
<h3>Çerez Tercihleri</h3>
<p>Tarayıcı ayarlarınızdan çerezleri yönetebilirsiniz. Çerezleri devre dışı bırakmak platformun bazı özelliklerini etkileyebilir.</p>`
  },
};

export default function StaticPage() {
  const { slug } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/site-content/page_${slug}`)
      .then(r => r.json())
      .then(data => {
        if (data.content && data.content.title) {
          setContent(data.content);
        } else {
          setContent(DEFAULT_CONTENT[slug] || { title: 'Sayfa Bulunamadı', body: '<p>Bu sayfa henüz oluşturulmamıştır.</p>' });
        }
        setLoading(false);
      })
      .catch(() => {
        setContent(DEFAULT_CONTENT[slug] || { title: 'Sayfa Bulunamadı', body: '' });
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#A3A3A3] font-ibm text-sm">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid={`static-page-${slug}`}>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="static-nav-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <span className="font-outfit text-xl font-bold text-white tracking-tight">Sosyal<span className="text-violet-400">.Team</span></span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-[#A3A3A3] hover:text-white font-ibm transition-colors" data-testid="back-to-home">
            <ArrowLeft size={14} /> Ana Sayfa
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-8" data-testid="page-title">
            {content?.title}
          </h1>
          <div
            className="prose prose-invert max-w-none font-ibm text-[#A3A3A3] leading-relaxed
              [&_h3]:text-white [&_h3]:font-outfit [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3
              [&_p]:mb-4 [&_p]:text-sm [&_p]:leading-relaxed
              [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ul]:mb-4
              [&_li]:text-sm
              [&_a]:text-violet-400 [&_a]:underline"
            data-testid="page-body"
            dangerouslySetInnerHTML={{ __html: content?.body || '' }}
          />
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#080808] py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs text-[#525252] font-ibm">&copy; 2026 Sosyal.Team. Tüm hakları saklıdır.</p>
        </div>
      </footer>
    </div>
  );
}
